import { useCallback, useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { AccountExpanded, CarrierConnectionExpanded } from '@engine/models'

import { AdminListLayout } from '#components/AdminListLayout'
import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel.js'
import { FilterInput } from '#components/adminFilters/FilterInput.js'
import { FilterRow } from '#components/adminFilters/FilterRow.js'
import { FilterSelect } from '#components/adminFilters/FilterSelect.js'
import { Slider } from '#components/Slider.js'
import { usePagedFetch } from '#hooks/useAdminResource'
import { useInfiniteAdminResource } from '#hooks/useInfiniteAdminResource'
import { useIntersectionObserver } from '#hooks/useIntersectionObserver'

import { CarrierCredentialDetails } from './CarrierConnectionDetails.js'
import { CarrierConnectionsList } from './CarrierConnectionsList.js'

const CarrierConnectionsView = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const [selectedCredential, setSelectedCredential] =
    useState<CarrierConnectionExpanded | null>(null)
  const [search, setSearch] = useState(searchParams.get('search') || '')
  const [accountId, setAccountId] = useState(
    searchParams.get('accountId') || '',
  )

  // Fetch accounts for the select dropdown
  const { items: accounts } = usePagedFetch<AccountExpanded>('accounts', {
    limit: 500,
  })

  const {
    items: credentials,
    isLoading,
    isFetchingNextPage,
    hasNextPage,
    error,
    refresh,
    fetchNextPage,
  } = useInfiniteAdminResource<CarrierConnectionExpanded>(
    'carrier-connections',
    {
      search,
      accountId,
      limit: 50,
    },
  )

  // Intersection observer to trigger loading more items
  const handleLoadMore = useCallback(() => {
    if (!isFetchingNextPage && hasNextPage) {
      fetchNextPage()
    }
  }, [isFetchingNextPage, hasNextPage, fetchNextPage])

  const sentinelRef = useIntersectionObserver(handleLoadMore, {
    enabled: hasNextPage && !isFetchingNextPage,
    threshold: 0.1,
  })

  // Sync filters to URL query params
  useEffect(() => {
    const params = new URLSearchParams()
    if (search) params.set('search', search)
    if (accountId) params.set('accountId', accountId)
    setSearchParams(params, { replace: true })
  }, [search, accountId, setSearchParams])

  const handleCredentialSelect = useCallback(
    (credential: CarrierConnectionExpanded) => {
      setSelectedCredential(credential)
    },
    [],
  )

  const handleCloseDetails = useCallback(() => {
    setSelectedCredential(null)
  }, [])

  return (
    <>
      <AdminListLayout
        title="Carrier Credentials"
        isLoading={isLoading}
        error={error}
        onRefresh={refresh}
        emptyMessage="No carrier credentials found"
        filters={
          <AdminFilters isLoading={isLoading} onRefresh={refresh}>
            <FilterRow>
              <FilterInput
                label="Search credentials"
                value={search}
                onChange={setSearch}
                placeholder="Search by carrier..."
              />
              <FilterSelect
                label="Account"
                value={accountId}
                onChange={setAccountId}
                options={[
                  { value: '', label: 'All Accounts' },
                  ...accounts
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(account => ({
                      value: account._id,
                      label: `${account.name} (${account._id})`,
                    })),
                ]}
              />
            </FilterRow>
          </AdminFilters>
        }
        listContent={
          credentials.length > 0 ? (
            <>
              <CarrierConnectionsList
                credentials={credentials}
                selectedId={selectedCredential?._id}
                onSelect={handleCredentialSelect}
              />
              {/* Sentinel element for infinite scroll */}
              <div ref={sentinelRef} className="h-4" />
              {/* Loading indicator */}
              {isFetchingNextPage ? (
                <div className="flex justify-center py-4">
                  <span className="loading loading-spinner loading-md" />
                </div>
              ) : null}
            </>
          ) : null
        }
      />
      <Slider
        isOpen={!!selectedCredential}
        onClose={handleCloseDetails}
        title={`${selectedCredential?.carrierCode?.toUpperCase()} Details`}
        width="40vw"
      >
        <CarrierCredentialDetails credential={selectedCredential} />
      </Slider>
    </>
  )
}

export { CarrierConnectionsView }
